'use client';

import { useEffect, useRef, useState, MouseEventHandler, ReactNode } from 'react';
import { Button, ComboBox, Key } from 'react-aria-components';

import { IconKitChevron } from '@/icon/kit/Chevron';
import { UiKitInputSearch } from '@/ui-kit/input-search';
import { Option, UiKitListbox } from '@/ui-kit/listbox';

import { styles } from './UiKitSelect.styles';

export interface UiKitSelectProps<T extends Option> {
  options: T[];
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  onChange?: (option: T | null) => void;
  errorMessage?: string;
  selected?: T | null;
  defaultSelected?: T | null;
  className?: string;
  withBorder?: boolean;
  noInput?: boolean;
  inputClassName?: string;
  name?: string;
  renderOption?: (option: T) => ReactNode;
  classNameStateListBox?: 'default' | 'map';
  icons?: Record<string, React.ElementType>;
}

export function UiKitSelect<T extends Option>({
  options,
  disabled,
  onChange,
  helperText,
  errorMessage,
  placeholder,
  selected,
  defaultSelected,
  label,
  withBorder = true,
  className,
  noInput = false,
  inputClassName,
  name,
  renderOption,
  icons,
  classNameStateListBox
}: UiKitSelectProps<T>) {
  const slots = styles({ noInput });
  const triggerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const Icon = selected?.icon && icons && icons[selected.icon];

  const handleChange = (key: Key | null) => {
    const option = options.find((option) => option.value === key);
    onChange?.(option ?? null);
    setInputValue(option?.label ?? '');
  };

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!inputValue) {
      setInputValue(selected?.label ?? '');
    }
  }, [inputValue, selected?.label]);

  const handleInputClick: MouseEventHandler<HTMLInputElement> = (event) => {
    if (!noInput) {
      return;
    }

    event.preventDefault();
    buttonRef.current?.click();
  };

  return (
    <div className={slots.base({ className })}>
      <ComboBox
        selectedKey={selected?.value}
        defaultSelectedKey={defaultSelected?.value}
        isDisabled={disabled}
        onSelectionChange={handleChange}
        isInvalid={!!errorMessage}
        menuTrigger="focus"
        inputValue={inputValue}
        name={name}
      >
        <UiKitInputSearch
          ref={triggerRef}
          value={selected?.label}
          className={slots.input({ className: inputClassName })}
          helperText={helperText}
          errorMessage={errorMessage}
          placeholder={placeholder}
          label={label}
          withBorder={withBorder}
          classNameState={classNameStateListBox}
          isEmpty={inputValue == ''}
          onClick={handleInputClick}
          startAdornment={
            Icon && (
              <Icon
                width={36}
                height={36}
              />
            )
          }
          endAdornment={
            <Button
              ref={buttonRef}
              className={slots.button()}
            >
              <IconKitChevron className={slots.icon()} />
            </Button>
          }
          readOnly={noInput}
        />
        <UiKitListbox
          options={options}
          triggerRef={triggerRef}
          renderOption={renderOption}
          classNameState={classNameStateListBox}
        />
      </ComboBox>
    </div>
  );
}
